import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import axios from 'axios';
import { 
  Trophy, 
  TrendingUp,
  TrendingDown,
  Medal,
  Crown
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AGE_CATEGORIES = ['All', 'U8', 'U10', 'U12', 'U14', 'U16', 'U18', 'Adults'];
const GENDERS = ['All', 'Boys', 'Girls', 'Mixed'];

export const Leaderboard = () => {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ageFilter, setAgeFilter] = useState('All');
  const [genderFilter, setGenderFilter] = useState('All');

  useEffect(() => {
    fetchLeaderboard();
  }, [ageFilter, genderFilter]);

  const fetchLeaderboard = async () => {
    try {
      const params = new URLSearchParams();
      if (ageFilter !== 'All') params.append('age_category', ageFilter);
      if (genderFilter !== 'All') params.append('gender', genderFilter);
      
      const response = await axios.get(`${API}/leaderboard?${params}`);
      setTeams(response.data);
    } catch (error) {
      console.error('Failed to fetch leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getWinRate = (wins, losses) => {
    const total = wins + losses;
    if (total === 0) return 0;
    return Math.round((wins / total) * 100);
  };

  const getPointDiff = (scored, allowed) => {
    const diff = scored - allowed;
    return diff >= 0 ? `+${diff}` : diff.toString();
  };

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1: return <Crown className="w-6 h-6 text-yellow-500" />;
      case 2: return <Medal className="w-6 h-6 text-gray-400" />;
      case 3: return <Medal className="w-6 h-6 text-amber-600" />;
      default: return <span className="font-mono text-lg font-bold text-muted-foreground">{rank}</span>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="leaderboard-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black">
            <span className="text-primary">LEADERBOARD</span>
          </h1>
          <p className="text-muted-foreground mt-1">
            Team rankings by wins and point differential
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-8" data-testid="filters-card">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <Select value={ageFilter} onValueChange={setAgeFilter}>
              <SelectTrigger className="w-full sm:w-[160px]" data-testid="age-filter">
                <SelectValue placeholder="Age Category" />
              </SelectTrigger>
              <SelectContent>
                {AGE_CATEGORIES.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat === 'All' ? 'All Ages' : cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={genderFilter} onValueChange={setGenderFilter}>
              <SelectTrigger className="w-full sm:w-[160px]" data-testid="gender-filter">
                <SelectValue placeholder="Gender" />
              </SelectTrigger>
              <SelectContent>
                {GENDERS.map(g => (
                  <SelectItem key={g} value={g}>{g === 'All' ? 'All Genders' : g}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Leaderboard */}
      <Card data-testid="leaderboard-card">
        <CardHeader>
          <CardTitle className="text-xl font-bold flex items-center gap-2">
            <Trophy className="w-5 h-5 text-primary" />
            Rankings
          </CardTitle>
        </CardHeader>
        <CardContent>
          {teams.length > 0 ? (
            <div className="space-y-2">
              {/* Header Row */}
              <div className="grid grid-cols-12 gap-4 px-4 py-2 text-sm font-medium text-muted-foreground border-b">
                <div className="col-span-1">#</div>
                <div className="col-span-4">Team</div>
                <div className="col-span-2 text-center">Record</div>
                <div className="col-span-2 text-center">Win %</div>
                <div className="col-span-2 text-center">+/-</div>
                <div className="col-span-1"></div>
              </div>
              
              {/* Team Rows */}
              {teams.map((team, index) => (
                <div
                  key={team.id}
                  className={`grid grid-cols-12 gap-4 px-4 py-4 rounded-lg items-center ${
                    index < 3 ? 'bg-primary/5 border border-primary/20' : 'hover:bg-muted/50'
                  }`}
                  data-testid={`leaderboard-row-${index}`}
                >
                  <div className="col-span-1 flex justify-center">
                    {getRankIcon(index + 1)}
                  </div>
                  
                  <div className="col-span-4">
                    <p className="font-bold">{team.name}</p>
                    <div className="flex gap-2 mt-1">
                      <Badge variant="outline" className="text-xs">
                        {team.age_category}
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        {team.gender}
                      </Badge>
                    </div>
                  </div>
                  
                  <div className="col-span-2 text-center">
                    <span className="font-mono">
                      <span className="text-green-500">{team.wins}</span>
                      <span className="text-muted-foreground"> - </span>
                      <span className="text-red-500">{team.losses}</span>
                    </span>
                  </div>
                  
                  <div className="col-span-2 text-center">
                    <span className="font-mono font-bold">
                      {getWinRate(team.wins, team.losses)}%
                    </span>
                  </div>
                  
                  <div className="col-span-2 text-center">
                    <span className={`font-mono font-bold flex items-center justify-center gap-1 ${
                      team.points_scored - team.points_allowed >= 0 ? 'text-green-500' : 'text-red-500'
                    }`}>
                      {team.points_scored - team.points_allowed >= 0 ? (
                        <TrendingUp className="w-4 h-4" />
                      ) : (
                        <TrendingDown className="w-4 h-4" />
                      )}
                      {getPointDiff(team.points_scored, team.points_allowed)}
                    </span>
                  </div>
                  
                  <div className="col-span-1 text-right text-xs text-muted-foreground">
                    {team.tournaments_played?.length || 0} T
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Trophy className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <h3 className="text-xl font-bold mb-2">No Teams Yet</h3>
              <p>Teams will appear here after playing games</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
